import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs';
import {
  SENTIMENT_ANALYSIS_CONFIG,
  type SentimentSettingsTabKey,
} from '../sentiment-analysis.config';
import { SentimentAnalysisStatsService } from '../../../services/sentiment-analysis-stats/sentiment-analysis-stats.service';
import { AppStateService } from '../../../services/helpers/app-state.service';
import { FeedsService } from '../../../services/feeds/feeds.service';
import { SentimentAnalysisTaxonomyPreviewComponent } from '../sentiment-analysis-taxonomy-preview/sentiment-analysis-taxonomy-preview.component';
import {
  buildProjectConfigFromDraft,
  cohortFieldOptionsFromBrandDetails,
  createEmptySetupDraft,
  SUMMARY_PRECOMPUTE_SENTIMENT_CHOICES,
  resolveRatingAlignmentFromMainRaterType,
  summarySentimentLabel,
  toggleSummaryPrecomputeSentiment,
  resolveRatingAlignmentFromProjectConfig,
  resolveBrandDetailsForCohortSetup,
  setupDraftFromProjectConfig,
  taxonomyThemeNamesFromDraft,
  type BrandCohortFieldOption,
  type RatingAlignmentScale,
  type SentimentSetupDraft,
} from '../sentiment-analysis-setup.model';

type CohortSelectionTarget = 'heatmap' | 'summary';

@Component({
  selector: 'app-sentiment-analysis-settings-home',
  standalone: true,
  imports: [CommonModule, FormsModule, SentimentAnalysisTaxonomyPreviewComponent],
  templateUrl: './sentiment-analysis-settings-home.component.html',
  styleUrls: [
    './sentiment-analysis-settings-home.component.scss',
    '../sentiment-analysis-getting-started/sentiment-analysis-getting-started.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SentimentAnalysisSettingsHomeComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly statsService = inject(SentimentAnalysisStatsService);
  private readonly appStateService = inject(AppStateService);
  private readonly feedsService = inject(FeedsService);

  readonly config = SENTIMENT_ANALYSIS_CONFIG;
  readonly panelTabs = this.config.copy.settingsPanelTabs;
  readonly industryCopy = this.config.copy.gettingStartedIndustry;
  readonly taxonomyCopy = this.config.copy.gettingStartedTaxonomy;
  readonly heatmapCohortsCopy = this.config.copy.gettingStartedHeatmapCohorts;
  readonly summaryCohortsCopy = this.config.copy.gettingStartedSummaryCohorts;
  readonly promptCopy = this.config.copy.gettingStartedPrompt;
  readonly summaryCopy = this.config.copy.gettingStartedSummary;
  readonly summarySentimentChoices = SUMMARY_PRECOMPUTE_SENTIMENT_CHOICES;
  readonly buttons = this.config.copy.gettingStartedButtons;

  readonly taxonomyThemeModeOptions = [
    { value: 'names_only', label: 'Names only' },
    { value: 'names_and_descriptions', label: 'Names and descriptions' },
    { value: 'names_descriptions_and_keywords', label: 'Names, descriptions, and keywords' },
  ] as const;

  @Input() canWrite = false;
  @Input() brandIdForApi = '';
  @Input() brandDetails: Record<string, unknown> | null = null;

  @Output() readonly settingsSaved = new EventEmitter<string>();
  @Output() readonly configurationSaved = new EventEmitter<string>();
  @Output() readonly settingsMessage = new EventEmitter<{ text: string; kind: 'info' | 'error' | 'warn' }>();

  panelOpen = false;
  setupMode = false;
  loadingPanel = false;
  configLoaded = false;
  loadError: string | null = null;
  saving = false;
  activeTab: SentimentSettingsTabKey = 'industry';
  loadedConfigVersion: number | string | null = null;

  draft: SentimentSetupDraft = createEmptySetupDraft();

  cohortFieldOptions: BrandCohortFieldOption[] = [];
  selectedHeatmapCohortFieldIds = new Set<string>();
  selectedSummaryCohortFieldIds = new Set<string>();
  cohortFieldsLoading = false;
  private heatmapCohortUserTouched = false;
  private summaryCohortUserTouched = false;
  ratingAlignmentResolved: RatingAlignmentScale | null = null;

  taxonomyCsvFileName = '';
  taxonomyPreviewError: string | null = null;
  taxonomyPreviewLoading = false;
  summaryThemeToAdd = '';

  get currentVersionLabel(): string {
    const version = this.loadedConfigVersion ?? '?';
    return this.config.copy.settingsPanelCurrentVersion.replace('{{version}}', String(version));
  }

  openPanel(opts?: { setupMode?: boolean; initialTab?: SentimentSettingsTabKey }): void {
    this.setupMode = opts?.setupMode === true;
    this.activeTab = opts?.initialTab ?? 'industry';
    this.panelOpen = true;
    this.loadError = null;
    this.configLoaded = false;
    this.loadedConfigVersion = null;
    this.loadingPanel = true;
    this.draft = createEmptySetupDraft();
    this.resetCohortSelectionState();
    this.taxonomyCsvFileName = '';
    this.taxonomyPreviewError = null;
    this.cdr.markForCheck();

    const brandId = String(this.brandIdForApi || '').trim();
    if (!brandId) {
      this.loadingPanel = false;
      this.loadError = this.config.copy.settingsPanelLoadError;
      this.cdr.markForCheck();
      return;
    }

    if (!this.config.useInsightsEngine || !this.config.insightsLatestSentimentConfigPath) {
      this.loadingPanel = false;
      this.loadError = 'Insights engine is required to load configuration.';
      this.cdr.markForCheck();
      return;
    }

    this.statsService.clearSentimentConfigCache(brandId);
    this.statsService
      .getLatestSentimentConfig(brandId, { force: true })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const doc = result.document;
          const cfg = (doc?.config ?? null) as Record<string, unknown> | null;
          this.loadedConfigVersion = doc?.version ?? null;
          this.draft = setupDraftFromProjectConfig(cfg);
          this.applyCohortSetsFromDraft();
          this.refreshRatingFromBrandAndConfig(cfg);
          const hasTaxonomy = !!this.draft.taxonomy?.themes?.length;
          this.configLoaded = hasTaxonomy;
          this.loadingPanel = false;
          this.loadError = hasTaxonomy
            ? null
            : this.setupMode
              ? null
              : this.config.copy.settingsPanelNoConfig;

          this.loadCohortFieldsIfNeeded();
          this.onTabActivated(this.activeTab);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingPanel = false;
          this.configLoaded = false;
          this.loadError = this.config.copy.settingsPanelLoadError;
          this.draft = createEmptySetupDraft();
          this.cdr.markForCheck();
        },
      });
  }

  closePanel(): void {
    this.panelOpen = false;
    this.setupMode = false;
    this.loadingPanel = false;
    this.configLoaded = false;
    this.loadError = null;
    this.loadedConfigVersion = null;
    this.activeTab = 'industry';
    this.taxonomyCsvFileName = '';
    this.taxonomyPreviewError = null;
    this.cdr.markForCheck();
  }

  selectTab(tab: SentimentSettingsTabKey): void {
    this.activeTab = tab;
    this.onTabActivated(tab);
    this.cdr.markForCheck();
  }

  private onTabActivated(tab: SentimentSettingsTabKey): void {
    if (tab === 'heatmap_cohorts') {
      this.loadCohortFieldsIfNeeded();
      this.applyInitialCohortDefaultsFor('heatmap');
    }
    if (tab === 'summary') {
      this.loadCohortFieldsIfNeeded();
      this.applyInitialCohortDefaultsFor('summary');
      this.syncSummaryThemeAllowlistFromTaxonomy(false);
    }
  }

  private resetCohortSelectionState(): void {
    this.heatmapCohortUserTouched = false;
    this.summaryCohortUserTouched = false;
    this.selectedHeatmapCohortFieldIds = new Set<string>();
    this.selectedSummaryCohortFieldIds = new Set<string>();
  }

  private applyCohortSetsFromDraft(): void {
    this.selectedHeatmapCohortFieldIds = new Set(this.draft.heatmapCohortFieldIds);
    this.selectedSummaryCohortFieldIds = new Set(this.draft.summaryCohortFieldIds);
  }

  get taxonomyThemeNames(): string[] {
    return taxonomyThemeNamesFromDraft(this.draft);
  }

  get themesAvailableToAdd(): string[] {
    const inAllowlist = new Set(this.draft.summaryThemeAllowlist);
    return this.taxonomyThemeNames.filter((n) => !inAllowlist.has(n));
  }

  get isRatingAlignmentConfigured(): boolean {
    return this.ratingAlignmentResolved != null;
  }

  get canSave(): boolean {
    if (!this.canWrite || this.loadingPanel || !this.configLoaded) {
      return false;
    }
    const brandId = String(this.brandIdForApi || '').trim();
    if (!brandId || !this.config.useInsightsEngine) {
      return false;
    }
    const hasTaxonomy = !!this.draft.taxonomy?.themes?.length;
    const summaryCohortsOk =
      this.draft.summaryCohortFieldIds.length > 0 || this.cohortFieldOptions.length === 0;
    const allowlistOk =
      !this.draft.summaryPrecomputeEnabled ||
      this.draft.summaryThemeAllowlist.length > 0 ||
      !hasTaxonomy;
    const sentimentsOk =
      !this.draft.summaryPrecomputeEnabled || this.draft.summaryPrecomputeSentiments.length > 0;
    return (
      hasTaxonomy &&
      String(this.draft.industryName ?? '').trim().length > 0 &&
      summaryCohortsOk &&
      allowlistOk &&
      sentimentsOk &&
      this.isRatingAlignmentConfigured
    );
  }

  trackCohortField(_index: number, opt: BrandCohortFieldOption): string {
    return opt.fieldId;
  }

  isCohortFieldSelected(fieldId: string, target: CohortSelectionTarget): boolean {
    const set = target === 'heatmap' ? this.selectedHeatmapCohortFieldIds : this.selectedSummaryCohortFieldIds;
    return set.has(fieldId);
  }

  cohortSelectedCount(target: CohortSelectionTarget): number {
    const set = target === 'heatmap' ? this.selectedHeatmapCohortFieldIds : this.selectedSummaryCohortFieldIds;
    return set.size;
  }

  private resolveBrandForCohorts(): Record<string, unknown> | null {
    return resolveBrandDetailsForCohortSetup(
      this.brandDetails,
      this.appStateService.brandDetails() as Record<string, unknown> | null,
    );
  }

  private refreshRatingFromBrandAndConfig(config?: Record<string, unknown> | null): void {
    const bd = this.resolveBrandForCohorts();
    const fromBrand = resolveRatingAlignmentFromMainRaterType(bd?.['main_rater_type']);
    this.ratingAlignmentResolved =
      fromBrand ?? resolveRatingAlignmentFromProjectConfig(config ?? undefined);
  }

  private pruneCohortSet(
    current: Set<string>,
    userTouched: boolean,
    draftIds: string[],
    validIds: Set<string>,
  ): Set<string> {
    if (userTouched || current.size > 0) {
      return new Set([...current].filter((id) => validIds.has(id)));
    }
    if (draftIds.length > 0) {
      return new Set(draftIds.filter((id) => validIds.has(id)));
    }
    return current;
  }

  private setCohortFieldOptions(options: BrandCohortFieldOption[]): void {
    this.cohortFieldOptions = options;
    const labels = { ...this.draft.cohortLabels };
    for (const o of options) {
      labels[o.fieldId] = o.label;
    }
    this.draft.cohortLabels = labels;
    const validIds = new Set(options.map((o) => o.fieldId));
    this.selectedHeatmapCohortFieldIds = this.pruneCohortSet(
      this.selectedHeatmapCohortFieldIds,
      this.heatmapCohortUserTouched,
      this.draft.heatmapCohortFieldIds,
      validIds,
    );
    this.selectedSummaryCohortFieldIds = this.pruneCohortSet(
      this.selectedSummaryCohortFieldIds,
      this.summaryCohortUserTouched,
      this.draft.summaryCohortFieldIds,
      validIds,
    );
    this.syncDraftCohortIdsFromSets();
  }

  private syncDraftCohortIdsFromSets(): void {
    this.draft.heatmapCohortFieldIds = this.cohortFieldOptions
      .filter((o) => this.selectedHeatmapCohortFieldIds.has(o.fieldId))
      .map((o) => o.fieldId);
    this.draft.summaryCohortFieldIds = this.cohortFieldOptions
      .filter((o) => this.selectedSummaryCohortFieldIds.has(o.fieldId))
      .map((o) => o.fieldId);
  }

  private applyInitialCohortDefaultsFor(target: CohortSelectionTarget): void {
    if (!this.cohortFieldOptions.length) {
      return;
    }
    if (target === 'heatmap') {
      if (this.heatmapCohortUserTouched || this.selectedHeatmapCohortFieldIds.size > 0) {
        return;
      }
      if (this.draft.heatmapCohortFieldIds.length) {
        this.selectedHeatmapCohortFieldIds = new Set(this.draft.heatmapCohortFieldIds);
      }
    } else {
      if (this.summaryCohortUserTouched || this.selectedSummaryCohortFieldIds.size > 0) {
        return;
      }
      const ids =
        this.selectedHeatmapCohortFieldIds.size > 0
          ? [...this.selectedHeatmapCohortFieldIds]
          : this.draft.summaryCohortFieldIds.length
            ? [...this.draft.summaryCohortFieldIds]
            : [];
      this.selectedSummaryCohortFieldIds = new Set(ids);
    }
    this.syncDraftCohortIdsFromSets();
  }

  loadCohortFieldsIfNeeded(): void {
    const options = cohortFieldOptionsFromBrandDetails(this.resolveBrandForCohorts());
    this.setCohortFieldOptions(options);
    if (options.length) {
      return;
    }
    const brandId = String(this.brandIdForApi || '').trim();
    if (!brandId || this.cohortFieldsLoading) {
      return;
    }
    this.cohortFieldsLoading = true;
    this.cdr.markForCheck();
    this.feedsService
      .getFieldDefinitions({ brand_id: brandId })
      .pipe(
        take(1),
        finalize(() => {
          this.cohortFieldsLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res: unknown) => {
          const r = res as Record<string, unknown>;
          const data = r['data'] as Record<string, unknown> | undefined;
          const defs =
            (Array.isArray(data?.['field_definitions']) ? data['field_definitions'] : null) ??
            (Array.isArray(r['field_definitions']) ? r['field_definitions'] : []);
          const merged: Record<string, unknown> = {
            ...(this.resolveBrandForCohorts() ?? {}),
            field_definitions: defs,
          };
          this.setCohortFieldOptions(cohortFieldOptionsFromBrandDetails(merged));
        },
      });
  }

  onCohortCheckboxClick(
    fieldId: string,
    label: string,
    target: CohortSelectionTarget,
    event: MouseEvent,
  ): void {
    event.preventDefault();
    event.stopPropagation();
    const current = target === 'heatmap' ? this.selectedHeatmapCohortFieldIds : this.selectedSummaryCohortFieldIds;
    const next = new Set(current);
    if (next.has(fieldId)) {
      next.delete(fieldId);
    } else {
      next.add(fieldId);
    }
    if (target === 'heatmap') {
      this.heatmapCohortUserTouched = true;
      this.selectedHeatmapCohortFieldIds = next;
    } else {
      this.summaryCohortUserTouched = true;
      this.selectedSummaryCohortFieldIds = next;
    }
    this.draft.cohortLabels = { ...this.draft.cohortLabels, [fieldId]: label };
    this.syncDraftCohortIdsFromSets();
    this.cdr.detectChanges();
  }

  copySummaryCohortsFromHeatmap(): void {
    this.summaryCohortUserTouched = true;
    this.selectedSummaryCohortFieldIds = new Set(this.selectedHeatmapCohortFieldIds);
    this.syncDraftCohortIdsFromSets();
    this.cdr.detectChanges();
  }

  isSummarySentimentSelected(sentimentId: string): boolean {
    return this.draft.summaryPrecomputeSentiments.includes(String(sentimentId ?? '').trim().toLowerCase());
  }

  summarySentimentChoiceLabel(sentimentId: string): string {
    return summarySentimentLabel(sentimentId);
  }

  onSummarySentimentCheckboxClick(sentimentId: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.draft.summaryPrecomputeSentiments = toggleSummaryPrecomputeSentiment(
      this.draft.summaryPrecomputeSentiments,
      sentimentId,
    );
    this.cdr.detectChanges();
  }

  onTaxonomyCsvSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }
    this.taxonomyCsvFileName = file.name;
    this.taxonomyPreviewError = null;
    this.taxonomyPreviewLoading = true;
    this.cdr.markForCheck();
    this.statsService.previewTaxonomyFromCsvFile(file).subscribe({
      next: (taxonomy) => {
        this.taxonomyPreviewLoading = false;
        if (!taxonomy.themes.length) {
          this.taxonomyPreviewError =
            'No valid rows found. Add at least one theme row in the CSV.';
        } else {
          this.draft.taxonomy = taxonomy;
          this.syncSummaryThemeAllowlistFromTaxonomy(true);
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.taxonomyPreviewLoading = false;
        this.taxonomyPreviewError = String((err as Error)?.message || err || 'Failed to parse CSV');
        this.cdr.markForCheck();
      },
    });
  }

  private syncSummaryThemeAllowlistFromTaxonomy(resetToAll: boolean): void {
    const names = this.taxonomyThemeNames;
    if (!names.length) {
      this.draft.summaryThemeAllowlist = [];
      return;
    }
    if (resetToAll || this.draft.summaryThemeAllowlist.length === 0) {
      this.draft.summaryThemeAllowlist = [...names];
      return;
    }
    const valid = new Set(names);
    this.draft.summaryThemeAllowlist = this.draft.summaryThemeAllowlist.filter((n) => valid.has(n));
  }

  removeThemeFromAllowlist(themeName: string): void {
    const name = String(themeName ?? '').trim();
    if (!name) {
      return;
    }
    this.draft.summaryThemeAllowlist = this.draft.summaryThemeAllowlist.filter((t) => t !== name);
    this.cdr.markForCheck();
  }

  addSelectedThemeToAllowlist(): void {
    const name = String(this.summaryThemeToAdd ?? '').trim();
    if (!name || this.draft.summaryThemeAllowlist.includes(name)) {
      return;
    }
    if (!this.taxonomyThemeNames.includes(name)) {
      return;
    }
    this.draft.summaryThemeAllowlist = [...this.draft.summaryThemeAllowlist, name];
    this.summaryThemeToAdd = '';
    this.cdr.markForCheck();
  }

  addAllTaxonomyThemesToAllowlist(): void {
    this.draft.summaryThemeAllowlist = [...this.taxonomyThemeNames];
    this.summaryThemeToAdd = '';
    this.cdr.markForCheck();
  }

  saveAll(): void {
    if (!this.canSave) {
      return;
    }
    const brandId = String(this.brandIdForApi || '').trim();
    if (!brandId) {
      this.emitMessage('Missing project; cannot save settings.', 'error');
      return;
    }

    let configPatch: Record<string, unknown>;
    try {
      if (!this.ratingAlignmentResolved) {
        this.emitMessage(this.config.copy.gettingStartedAnalysisInput.notConfiguredLead, 'error');
        return;
      }
      configPatch = buildProjectConfigFromDraft(this.draft, this.ratingAlignmentResolved);
    } catch (err) {
      this.emitMessage(String((err as Error)?.message || err || 'Invalid configuration'), 'error');
      return;
    }

    this.saving = true;
    this.cdr.markForCheck();

    this.statsService
      .saveProjectConfig(brandId, configPatch)
      .pipe(
        take(1),
        finalize(() => this.finishSaving()),
      )
      .subscribe({
        next: (res) => {
          this.statsService.clearSentimentConfigCache(brandId);
          const version = res?.configuration_version ?? '?';
          this.loadedConfigVersion = version;
          this.emitMessage(`${this.config.copy.settingsPanelSaved} (version ${version}).`, 'info');
          this.closePanel();
          this.settingsSaved.emit(brandId);
          this.configurationSaved.emit(brandId);
        },
        error: (err: unknown) => {
          const msg =
            err && typeof err === 'object' && 'message' in err && typeof (err as { message?: string }).message === 'string'
              ? (err as { message: string }).message
              : 'Could not save configuration.';
          this.emitMessage(String(msg), 'error');
        },
      });
  }

  private finishSaving(): void {
    this.saving = false;
    this.cdr.markForCheck();
  }

  private emitMessage(text: string, kind: 'info' | 'error' | 'warn'): void {
    this.settingsMessage.emit({ text, kind });
  }
}
